import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { Link } from 'react-router-dom';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [registered, setRegistered] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  if (registered) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          {/* Logo and name at the top */}
          <div className="text-center mb-4">
            <Link to="/" className="navbar-brand navbar-brand-autodark">
              <img src="/favicon.svg" alt="BidMatch Logo" width="32" height="32" className="me-2" />
              <span className="brand-text">bidmatch.ai</span>
            </Link>
          </div>
          
          <div className="card card-md">
            <div className="card-body text-center py-4 p-sm-5">
              <span className="avatar avatar-xl mb-3 bg-primary-lt">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-mail" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z"></path>
                  <path d="M3 7l9 6l9 -6"></path>
                </svg>
              </span>
              <h1 className="mt-5">Check your email</h1>
              <p className="text-muted">
                We've sent a verification link to <strong>{userEmail}</strong>.<br />
                Please check your inbox and click the link to verify your account.
              </p>
              <div className="mt-4">
                <Link to="/login" className="btn btn-primary">Go to Login</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        {/* Logo and name at the top - centered with improved styling */}
        <div className="text-center mb-4">
          <Link to="/" className="navbar-brand navbar-brand-autodark d-inline-flex justify-content-center">
            <img src="/favicon.svg" alt="BidMatch Logo" width="48" height="48" className="me-2" />
            <span className="brand-text d-flex align-items-center">bidmatch.ai</span>
          </Link>
        </div>
        
        <div className="card card-md">
          <div className="card-body">
            <h2 className="h2 text-center mb-4">Create new account</h2>
            <Formik
              initialValues={{ 
                email: '', 
                password: '', 
                name: '',
                company: ''
              }}
              validationSchema={Yup.object({
                email: Yup.string().email('Invalid email address').required('Required'),
                password: Yup.string().required('Required'),
                name: Yup.string(),
                company: Yup.string()
              })}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                try {
                  await authService.register(values);
                  setUserEmail(values.email);
                  setRegistered(true);
                } catch (error) {
                  setStatus('Registration failed. Please try again.');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, handleChange, status, isSubmitting }) => (
                <Form>
                  <div className="mb-3">
                    <label className="form-label">Email address</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      onChange={handleChange}
                    />
                    {errors.email && touched.email && (
                      <div className="invalid-feedback d-block">{errors.email}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Company</label>
                    <input
                      type="text"
                      name="company"
                      className="form-control"
                      onChange={handleChange}
                    />
                    {errors.company && touched.company && (
                      <div className="invalid-feedback d-block">{errors.company}</div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      name="password"
                      className="form-control"
                      onChange={handleChange}
                    />
                    {errors.password && touched.password && (
                      <div className="invalid-feedback d-block">{errors.password}</div>
                    )}
                  </div>
                  {status && <div className="alert alert-danger">{status}</div>}
                  <div className="form-footer">
                    <button 
                      type="submit" 
                      className="btn btn-primary w-100" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Creating account...
                        </>
                      ) : (
                        'Create new account'
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <div className="text-center text-muted mt-3">
          Already have an account? <Link to="/login" tabIndex={-1}>Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;